import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ContratosActionsKeysEnum,
  getUrlParamsFetch,
  handleAxiosError,
} from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  Contrato,
  ContratosPagingRes,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchContratos = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetContratosParams> = {}) => {
  return useQuery({
    queryKey: [
      ContratosActionsKeysEnum.CONTRATOS,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getContratos(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
    gcTime:1000,
  });
};

export const useFetchContratosRefetchInterval = ({
  enabled = true,
  params,
  refetchInterval = 20000,
}: UseFetchEnabledParams<GetContratosParams> & {
  refetchInterval?: number;
}) => {
  return useQuery({
    queryKey: [
      ContratosActionsKeysEnum.CONTRATOS,
      ...Object.values(params ?? {}),
    ],
    queryFn: () => getContratos(params),
    enabled: enabled,
    refetchInterval: refetchInterval,
  });
};

export const useGetContrato = (id: string, enabled?:boolean) => {
  return useQuery({
    queryKey: ['contrato', id],
    queryFn: () => getContrato(id),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled//true
  });
};

export const useCreateContrato = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: CreateContratoParams<T>) => createContrato(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ContratosActionsKeysEnum.CONTRATOS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Contrato creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateContrato = <T>({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  fetchRespMessage =false,
  customSuccessCallback,
  enableLoading = true,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: UpdateContratoParams<T>) => updateContrato(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [ContratosActionsKeysEnum.CONTRATOS],
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          fetchRespMessage && res?.message ? res?.message : customSuccessMessage ?? 'Contrato actualizado correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      enableLoading && setIsGlobalLoading(false);
    },
  });
};

export const useDeleteContrato = () => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deleteContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ContratosActionsKeysEnum.CONTRATOS],
      });
      setAlert(
        true,
        'Contrato eliminado correctamente',
        AlertSeverityEnum.success
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetContratosParams = Partial<Contrato> & {
  page?: number;
  page_size?: number;

  identificacion?: string;
  razon_social?: string
  filterByState?: boolean;

  fecha_after?: string;
  fecha_before?: string;
  todos?:boolean
};

export type CreateContratoParamsForm = Omit<Contrato, 'id_contrato'>;
export type CreateContratoParams<T> = T;
export type UpdateContratoParams<T> = {
  id: string;
  data: T;
};

export const getContratos = async (params?: GetContratosParams) => {
  const stateParams = { ...params };

  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<ContratosPagingRes>(
    `${urlBackend}api/v1/contrato/?${queryParams}/`,
    true
  );
};

export const getContrato = async (id: string) => {
  try {
    return await get<Contrato>(`${urlBackend}api/v1/contrato/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

/**
 * Detalle de contrato para el flujo de tickets — no aplica las restricciones
 * de visibilidad por rol del ERP. Necesario para contratos importados de
 * Softnet (sin user_create / canal_venta_create) que el usuario soporte
 * necesita ver para abrir una orden de servicio.
 *
 * Solo usar dentro del modulo gestion-tickets; el resto del ERP debe seguir
 * usando getContrato/useGetContrato para respetar las reglas de permisos.
 */
export const getContratoParaTickets = async (id: string) => {
  try {
    return await get<Contrato>(
      `${urlBackend}api/v1/contrato/${id}/para-tickets/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const useGetContratoParaTickets = (id: string, enabled?: boolean) => {
  return useQuery({
    queryKey: ['contrato-para-tickets', id],
    queryFn: () => getContratoParaTickets(id),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!id,
  });
};

export const createContrato = async <T>(data: CreateContratoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Contrato>(`${urlBackend}api/v1/contrato/`, data, true);
};

export const updateContrato = async <T>({
  id,
  data,
}: UpdateContratoParams<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Contrato>(`${urlBackend}api/v1/contrato/${id}/`, data, true);
};

export const deleteContrato = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return remove<Contrato>(`${urlBackend}api/v1/contrato/${id}/`, true);
};

export type SuspendCurrentServiceData = Pick<Contrato, 'estado_contrato'|'estado_navegacion'>;

export const getForwardDocuments = async (idPreventa: string) => {
  try {
    return await get<Contrato>(`${urlBackend}api/v1/reenviar-correo-contrato-digital/${idPreventa}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};


interface ChangeIpv4ContratoParams {
  contrato_codigo: string;
  router_codigo?: string;
}
/**
 * Cambia el ipv4 de un contrato
 * @param {contrato_id: string, contrato_codigo: string}
 * @returns void
 */
export const changeIpv4Contrato = async (params:ChangeIpv4ContratoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  try {
    setIsGlobalLoading(true);
    return await post<Contrato>(`${urlBackend}api/v1/contrato-cambiar-ip/`, params, true);
  } catch (error) {
    handleAxiosError(error);
  } finally {
    setIsGlobalLoading(false);
  }
};

//customs
// export const getReporteAgendamiento = async (data: any) => {
//   try {
//     return await post<Blob>(
//       `${urlBackend}api/v1/reporte-contrato/`,
//       data,
//       true,
//       false
//     );
//   } catch (error) {
//     handleAxiosError(error);
//   }
// };